import { litDate } from './periodes';

/**
 * Employés sortis de l'effectif.
 *
 * Un employé ne se supprime pas. Le contrat de travail, les décomptes de
 * salaire et les certificats relèvent de la conservation décennale (CO art.
 * 958f), et l'ancien employé garde le droit de réclamer son certificat de
 * travail bien après son départ (CO art. 330a). Sa fiche quitte donc
 * l'effectif sans quitter l'application.
 */

/**
 * Dit si un employé a quitté l'entreprise.
 *
 * Le départ se lit sur la date de fin de contrat, et seulement une fois
 * celle-ci passée : un contrat qui se termine à la fin du mois est encore en
 * cours aujourd'hui, et l'employé figure toujours dans l'effectif — il est
 * payé, il pointe, il compte dans la masse salariale.
 *
 * @param {object} employe fiche de l'employé
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {boolean} vrai si le contrat est arrivé à son terme
 */
export function aQuitte(employe, maintenant = new Date()) {
  if (!employe?.dateFin) {
    return false;
  }
  const fin = litDate(employe.dateFin, maintenant) || lisISO(employe.dateFin);
  if (!fin) {
    return false;
  }
  const aujourdhui = new Date(
    maintenant.getFullYear(),
    maintenant.getMonth(),
    maintenant.getDate(),
  );
  return fin < aujourdhui;
}

/**
 * Délai pendant lequel l'accès de l'employé reste ouvert après la fin de son
 * contrat, pour reprendre ses fiches de salaire et certificats : un mois et
 * demi. Le serveur ferme ensuite ses comptes (réglages `ACCES_MOIS_APRES_FIN`
 * et `ACCES_JOURS_APRES_FIN`) : l'espace annonce la même date.
 */
export const MOIS_ACCES_APRES_FIN = 1;
export const JOURS_ACCES_APRES_FIN = 15;

/**
 * Dernier jour où l'accès de l'employé reste ouvert après la fin du contrat.
 *
 * @param {string} dateFin terme du contrat, au format ISO ou suisse
 * @param {number} [mois] délai, en mois
 * @param {Date} [maintenant] date de référence, pour lire une date sans année
 * @param {number} [jours] jours ajoutés après les mois
 * @returns {Date | null} le dernier jour d'accès, ou null sans date de fin
 */
export function finDAcces(
  dateFin,
  mois = MOIS_ACCES_APRES_FIN,
  maintenant = new Date(),
  jours = JOURS_ACCES_APRES_FIN,
) {
  if (!dateFin) return null;
  const fin = litDate(dateFin, maintenant) || lisISO(dateFin);
  if (!fin) return null;
  // Un mois plus tard, le même jour — ramené au dernier jour d'un mois plus
  // court, comme le serveur le compte (31 janvier → 28 février) — puis
  // quinze jours (→ 15 mars).
  const cible = new Date(fin.getFullYear(), fin.getMonth() + mois, 1);
  const dernier = new Date(cible.getFullYear(), cible.getMonth() + 1, 0).getDate();
  cible.setDate(Math.min(fin.getDate(), dernier) + jours);
  return cible;
}

/**
 * Relit une date au format ISO.
 *
 * Les fiches venues du serveur portent des dates ISO, celles saisies à l'écran
 * la forme suisse. Les deux doivent se lire.
 */
function lisISO(valeur) {
  const m = String(valeur || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/**
 * Sépare l'effectif de ceux qui l'ont quitté.
 *
 * @param {Array} employes tout le personnel
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {{effectif: Array, anciens: Array}}
 */
export function separeLEffectif(employes, maintenant = new Date()) {
  const effectif = [];
  const anciens = [];
  for (const e of employes || []) {
    (aQuitte(e, maintenant) ? anciens : effectif).push(e);
  }
  return { effectif, anciens };
}

/**
 * Collaborateurs qui peuvent encore signer au nom de l'entreprise.
 *
 * @param {Array} employes tout le personnel
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {Array} l'effectif, sans les fiches désactivées ni les départs
 */
export function collaborateursEnPoste(employes, maintenant = new Date()) {
  return separeLEffectif(employes, maintenant).effectif.filter(
    (e) => e && e.actif !== false && e.parti !== true,
  );
}

/**
 * Coordonnées d'un collaborateur, sous la forme d'un destinataire.
 *
 * Une décharge se fait aussi signer en interne — l'ouvrier qui accepte de
 * travailler sur une installation qu'il sait vétuste. Ses coordonnées sont
 * déjà dans sa fiche : les ressaisir invite la faute de frappe. La forme rendue
 * est celle d'un contact lu dans le carnet d'adresses (`lib/contacts`), pour
 * que le formulaire se remplisse de la même façon, d'où que vienne la personne.
 *
 * @param {object} employe fiche du collaborateur
 * @returns {{nom: string, adr: string, npa: string, tel: string, email: string}}
 *   les champs trouvés, chacun vide à défaut
 */
export function coordonneesDuCollaborateur(employe) {
  const e = employe || {};
  return {
    nom: [e.prenom, e.nom].filter(Boolean).join(' ').trim(),
    adr: e.adresse || '',
    npa: [e.npa, e.ville].filter(Boolean).join(' ').trim(),
    tel: e.tel || '',
    email: e.email || '',
  };
}

/**
 * Code de connexion sur chantier saisi dans Réglages → Accès, prêt à être
 * porté par la fiche de l'employé.
 *
 * La carte « Ajouter un collaborateur » demandait un code à quatre chiffres
 * mais ne le gardait que dans les réglages de la société : la fiche envoyée au
 * serveur n'en avait aucun, et l'ouvrier lisait « Aucun employé de cette
 * entreprise ne dispose d'un code ». Seul un code complet part : un champ
 * vide ou inachevé ne doit ni retirer ni écraser celui déjà enregistré.
 *
 * @param {string} [pin] saisie du formulaire
 * @returns {{codePin?: string}} le champ à fusionner dans la fiche, ou rien
 */
export function codePinSaisi(pin) {
  const code = String(pin ?? '').trim();
  return /^\d{4}$/.test(code) ? { codePin: code } : {};
}

/**
 * Collaborateurs de l'écran « Équipe & accès », sans leur code PIN.
 *
 * Le code saisi dans les réglages était recopié dans `soc.membres`, que le
 * sac de paramètres de la société enregistre en clair : il se lisait en base,
 * dans chaque sauvegarde et dans la réponse de `/api/societe`. Il ne vit que
 * sur la fiche de l'employé, où il est chiffré. Retiré à l'écriture comme à la
 * lecture : un ancien enregistrement relu ne le remontre pas, et le prochain
 * enregistrement l'efface.
 *
 * @param {unknown} membres collaborateurs tels que l'écran les tient
 * @returns {unknown} les mêmes, sans clé `pin`
 */
export function membresSansCode(membres) {
  if (!Array.isArray(membres)) return membres;
  return membres.map((m) => {
    if (!m || typeof m !== 'object' || !('pin' in m)) return m;
    const { pin: _pin, ...reste } = m;
    return reste;
  });
}
